import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  Input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  untracked,
} from '@angular/core';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { FontWeightOption, NumberPrecision } from '@blaze-shared/api-contracts-domain';
import {
  type ElementFormState,
  ElementLabelPropsWithPositionAndWidth,
  ElementLabelStyleProps,
  FontOptions,
  InputElementMetaUiFeatures,
  makeFontStyles,
  makeFontStyleStyles,
} from '@blaze-shared/elements-domain';
import { getBackground, getBorderColor } from '@blaze-shared/elements-domain';
import { isDatasetLinkValue } from '@blaze-shared/schema-domain';
import { InplaceEditComponent, safeDetectChanges } from '@blaze-shared/shared-ui';
import { isNullish, useEmitTracker } from '@blaze-shared/shared-util';
import { FALLBACK_THEME } from '@blaze-shared/theming-domain';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { ElementLabelComponent } from '../../components/element-label/element-label.component';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { SliderFormat, SliderPropsModel, SliderValuePosition } from './slider.models';
import {
  clampHandleSize,
  clampTrackWeight,
  dataValueToInternal,
  formatSliderValue,
  FormatSliderValueOptions,
  getSliderTickValues,
  internalValueToData,
  resolveStep,
  snapToStep,
  toSliderFormat,
} from './slider.utils';

const sliderFallbackTheme = FALLBACK_THEME.value;

type SliderConfig = {
  min: number;
  max: number;
  step: number | string | undefined;
  defaultValue: number | undefined;
};

@UntilDestroy()
@Component({
  selector: 'be-slider',
  imports: [ElementLabelComponent, InplaceEditComponent, MatSlider, MatSliderThumb],
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--be-slider-handle-size]': 'handleSizePx()',
    '[style.--be-slider-track-weight]': 'trackWeightPx()',
    '[style.--be-slider-handle-fill]': 'handleFill()',
    '[style.--be-slider-handle-outline]': 'handleOutline()',
    '[style.--be-slider-handle-outline-width]': 'handleOutlineWidth()',
    '[style.--be-slider-track-active]': 'trackActive()',
    '[style.--be-slider-track-inactive]': 'trackInactive()',
    '[style.--be-slider-tick-color]': 'tickColor()',
    '[style.--be-slider-tick-label-height]': 'tickLabelRowHeight()',
    '[class.be-slider--with-tick-labels]': 'showTickLabels()',
  },
})
export class SliderComponent
  extends CanvasElementBaseComponent<'slider'>
  implements CanvasElementComponent<'slider'>, OnInit, OnChanges
{
  @Input() value: number | undefined;
  @Input() formState?: ElementFormState;

  private readonly outputEmitTracker = useEmitTracker();
  private readonly schemaUpdateTracker = useEmitTracker();

  private readonly config = signal<SliderConfig>({
    min: 0,
    max: 100,
    step: 1,
    defaultValue: undefined,
  });

  private readonly contentFormat = signal<SliderFormat>('number');
  private readonly contentPrecision = signal<NumberPrecision | undefined>(undefined);
  private readonly contentCurrency = signal('USD');

  private readonly featureFormat = signal<SliderFormat | undefined>(undefined);
  private readonly featurePrecision = signal<NumberPrecision | undefined>(undefined);
  private readonly featureCurrency = signal<string | undefined>(undefined);
  readonly handleSizePx = signal('15px');
  readonly trackWeightPx = signal('4px');
  readonly handleFill = signal(sliderFallbackTheme.accent);
  readonly handleOutline = signal(sliderFallbackTheme.canvas);
  readonly handleOutlineWidth = signal('2px');
  readonly trackActive = signal(sliderFallbackTheme.accent);
  readonly trackInactive = signal(sliderFallbackTheme.complementary);
  readonly tickColor = signal(`${sliderFallbackTheme.value}80`);
  readonly valueColor = signal(sliderFallbackTheme.value);
  readonly tickLabelRowHeight = signal('16px');

  readonly elementKeyRoot = 'b-slider';

  elementKey!: string;
  labelEnabled = true;
  labelText = '';
  isReadonly = false;

  readonly format = computed(() => this.featureFormat() ?? this.contentFormat());
  readonly precision = computed(() => this.featurePrecision() ?? this.contentPrecision());
  readonly currency = computed(() => this.featureCurrency() ?? this.contentCurrency());

  readonly valuePosition = signal<SliderValuePosition>('side');
  readonly showRangeLabels = signal(true);
  readonly showTickMarks = signal(true);
  readonly showTickLabels = signal(false);

  readonly dataValue = signal<number | undefined>(undefined);

  readonly step = computed(() => resolveStep(this.config().step, this.format()));

  readonly min = computed(() => this.config().min);
  readonly max = computed(() => this.config().max);

  readonly sliderValue = computed(() => {
    const data = this.dataValue();
    const internal = data == null ? undefined : dataValueToInternal(data, this.format());
    const raw = internal ?? this.config().defaultValue ?? this.min();
    return snapToStep(raw, this.min(), this.max(), this.step());
  });

  readonly formatOptions = computed(
    (): FormatSliderValueOptions => ({
      format: this.format(),
      precision: this.precision(),
      currency: this.currency(),
    }),
  );

  readonly displayValue = computed(() =>
    formatSliderValue(this.sliderValue(), this.formatOptions()),
  );

  readonly displayMin = computed(() => formatSliderValue(this.min(), this.formatOptions()));
  readonly displayMax = computed(() => formatSliderValue(this.max(), this.formatOptions()));

  readonly tickLabelItems = computed(() => {
    const min = this.min();
    const max = this.max();

    return getSliderTickValues(min, max, this.step()).map((value) => ({
      value,
      label: formatSliderValue(value, this.formatOptions()),
    }));
  });

  styles: Record<string, string> = {};
  inputStyles: Record<string, string> = {};
  validationStyles: Record<string, string> = {};
  valueStyles: Record<string, string> = {};
  limitsStyles: Record<string, string> = {};
  tickLabelStyles: Record<string, string> = {};

  get hasValidationError(): boolean | undefined {
    return this.formState?.errors?.required;
  }

  get propsContent(): SliderPropsModel['content'] | undefined {
    return this.props?.content;
  }

  get propsStyle(): SliderPropsModel['style'] | undefined {
    return this.props?.style;
  }

  get title(): ElementLabelPropsWithPositionAndWidth | undefined {
    return this.propsContent?.title;
  }

  get labelBlock(): ElementLabelStyleProps | undefined {
    return this.propsStyle?.labelBlock;
  }

  get titleColor(): string | undefined {
    return this.propsStyle?.general?.title?.color1;
  }

  get titleIconColor(): string | undefined {
    return this.title?.iconColor?.color1;
  }

  get titlePosition(): string {
    return this.title?.position ?? 'left';
  }

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);

    effect(() => {
      const value = this.sliderValue();
      untracked(() => this.updateOutput(value));
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      if (!this.elementKey) {
        this.elementKey = `${this.elementKeyRoot}-${this.elementId}`;
      }

      if (this.schemaUpdateTracker.shouldEmit(this.getEventSchema('change'))) {
        this.setDefaultEventSchema('change');
      }

      this.readContentProps();
      this.updateStyles();
    }

    if (changes?.['value']) {
      const external = this.parseNumber(this.value);
      if (external != null) {
        this.dataValue.set(external);
      }
    }
  }

  ngOnInit(): void {
    this.getElementUiFeatures<InputElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText = (features.title ?? '') + this.getRequiredSuffix(features);
        this.isReadonly = features.isReadOnly || !!this.props?.content?.options?.readonly;

        this.featureFormat.set(toSliderFormat(features.format));
        this.featurePrecision.set(features.precision);
        this.featureCurrency.set(features.currency);

        safeDetectChanges(this.cdr);
      });
  }

  onSliderInput(rawValue: number): void {
    this.dataValue.set(internalValueToData(rawValue, this.format()));
  }

  onValueCommitted(value: string): void {
    const parsed = this.parseNumber(value);
    if (parsed != null) {
      this.dataValue.set(internalValueToData(parsed, this.format()));
    }
  }
  private readContentProps(): void {
    this.labelEnabled = this.props?.content?.title?.enabled ?? true;
    const { options, display } = this.props?.content || {};

    let min = Number(options?.min ?? 0);
    let max = Number(options?.max ?? 100);
    if (min > max) {
      [min, max] = [max, min];
    }
    this.config.set({
      min,
      max,
      step: options?.step,
      defaultValue: this.parseNumber(options?.defaultValue),
    });

    this.contentFormat.set(options?.format ?? 'number');
    this.contentPrecision.set(options?.precision);
    this.contentCurrency.set(options?.currency ?? 'USD');
    this.valuePosition.set(display?.valuePosition ?? 'side');
    this.showRangeLabels.set(display?.showRangeLabels ?? true);
    this.showTickMarks.set(display?.showTickMarks ?? false);
    this.showTickLabels.set(display?.showTickLabels ?? false);
  }

  private parseNumber(next: unknown): number | undefined {
    if (isDatasetLinkValue(next)) {
      next = Number(next.display);
    }
    if (isNullish(next) || Number.isNaN(Number(next))) {
      return undefined;
    }
    return Number(next);
  }

  private updateOutput(internalValue: number): void {
    const schema = this.getEventSchema('change');
    if (!schema) {
      return;
    }

    const data = internalValueToData(internalValue, this.format());

    const isInitialData = !this.outputEmitTracker.hasPrevious();
    if (!this.outputEmitTracker.shouldEmit(data)) {
      return;
    }

    this.output.emit({
      eventType: 'change',
      data,
      isInitialData,
      schema,
    });
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { title } = content || {};
    const { general, handle, track, ticks, value, limits, tickLabels, labelBlock } = style || {};
    if (!style || !title || !general || !handle || !track || !value || !limits || !labelBlock) {
      return;
    }

    const handleSize = clampHandleSize(Number.parseInt(handle.size, 10) || 20);
    const trackWeight = clampTrackWeight(Number.parseInt(track.weight, 10) || 4, handleSize);

    this.handleSizePx.set(`${handleSize}px`);
    this.trackWeightPx.set(`${trackWeight}px`);
    this.handleFill.set(getBackground(handle.fill));
    this.handleOutline.set(getBorderColor(handle.outline));
    this.handleOutlineWidth.set(handle.outlineWidth);
    this.trackActive.set(getBackground(track.active));
    this.trackInactive.set(getBackground(track.inactive));
    const tickColorSource =
      ticks?.color?.selectorType && ticks.color.selectorType !== 'none'
        ? ticks.color
        : track.inactive;
    this.tickColor.set(getBackground(tickColorSource));
    this.valueColor.set(value.color.color1);
    const limitsFont: FontOptions = limits.font ?? {
      fontFamily: labelBlock.fontFamily ?? '$t.fontFamily',
      fontWeight: (labelBlock.fontWeight ?? '$t.fontWeight') as FontWeightOption,
      fontSize: '11px',
      lineHeight: labelBlock.lineHeight ?? '$t.lineHeight',
      style: [],
    };
    this.limitsStyles = {
      color: limits.color.color1,
      ...makeFontStyles(limitsFont),
      ...makeFontStyleStyles(limitsFont.style),
      ['font-weight']: this.fontsService.getFontWeight(
        limitsFont.fontFamily,
        limitsFont.fontWeight,
      ),
    };

    const tickLabelFont: FontOptions = tickLabels?.font ?? {
      fontFamily: labelBlock.fontFamily ?? '$t.fontFamily',
      fontWeight: (labelBlock.fontWeight ?? '$t.fontWeight') as FontWeightOption,
      fontSize: '11px',
      lineHeight: labelBlock.lineHeight ?? '$t.lineHeight',
      style: [],
    };
    this.tickLabelRowHeight.set(tickLabelFont.lineHeight ?? tickLabelFont.fontSize ?? '16px');
    this.tickLabelStyles = {
      color: (tickLabels?.color ?? limits.color).color1,
      ...makeFontStyles(tickLabelFont),
      ['font-weight']: this.fontsService.getFontWeight(
        tickLabelFont.fontFamily,
        tickLabelFont.fontWeight,
      ),
    };

    const styles: Record<string, string> = {};
    const inputStyles: Record<string, string> = {};
    const validationStyles: Record<string, string> = {};

    if (title.position === 'top') {
      styles['flex-direction'] = 'column';
    } else if (title.position === 'right') {
      styles['flex-direction'] = 'row-reverse';
    }

    this.styles = styles;
    this.inputStyles = inputStyles;
    this.validationStyles = validationStyles;
    this.valueStyles = {
      color: this.valueColor(),
      ...makeFontStyles(value.font),
      ...makeFontStyleStyles(value.font?.style),
      ['font-weight']: this.fontsService.getFontWeight(
        value.font.fontFamily,
        value.font.fontWeight,
      ),
    };
  }
}
